import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { FiCheckCircle } from "react-icons/fi";

const ConfirmSubmitDialog = ({
    open,
    onClose,
    onConfirm,
    isLoading = false,
    title = "Submit your profile?",
    description = "Your profile, availability, and pricing will be sent for review. Editing is locked until approved.",
    confirmLabel = "Yes, submit for review",
    cancelLabel = "Go back and review",
}) => {
    const cancelRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === "Escape" && !isLoading) onClose();
        };
        document.addEventListener("keydown", onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        cancelRef.current?.focus();
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = prev;
        };
    }, [open, isLoading, onClose]);

    if (typeof document === "undefined" || !open) return null;

    return createPortal(
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
            role="presentation"
        >
            <div
                className="absolute inset-0 bg-black/40 backdrop-blur-[2px] cursor-default"
                onClick={() => !isLoading && onClose()}
                aria-hidden
            />
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="confirm-submit-title"
                aria-describedby="confirm-submit-desc"
                className="relative w-full max-w-[440px] rounded-2xl bg-white shadow-2xl shadow-black/15 ring-1 ring-black/5 overflow-hidden"
            >
                <div className="p-6 sm:p-8">
                    <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-nt-blue/10 text-nt-blue">
                        <FiCheckCircle size={26} strokeWidth={2} aria-hidden />
                    </div>
                    <h2
                        id="confirm-submit-title"
                        className="text-center text-lg font-semibold text-nt-blue sm:text-xl"
                    >
                        {title}
                    </h2>
                    <div id="confirm-submit-desc" className="mt-2 space-y-2">
                        <p className="text-center text-sm text-nt-grey leading-relaxed">
                            {description}
                        </p>
                        <p className="text-center text-sm text-nt-blue/80 leading-relaxed">
                            Once approved, your profile becomes active and visible to students.
                        </p>
                    </div>
                    <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                        <button
                            ref={cancelRef}
                            type="button"
                            disabled={isLoading}
                            onClick={onClose}
                            className="w-full sm:w-auto min-h-[44px] rounded-xl border-2 border-nt-grey-light px-5 py-2.5 text-sm font-semibold text-nt-blue hover:bg-nt-grey-light/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-nt-blue focus-visible:ring-offset-2 transition-colors disabled:opacity-60 sm:min-w-[140px]"
                        >
                            {cancelLabel}
                        </button>
                        <button
                            type="button"
                            disabled={isLoading}
                            onClick={onConfirm}
                            className="w-full sm:w-auto min-h-[44px] rounded-xl bg-nt-blue px-5 py-2.5 text-sm font-semibold text-white hover:opacity-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-nt-blue focus-visible:ring-offset-2 transition-opacity disabled:opacity-60 sm:min-w-[180px]"
                        >
                            {isLoading ? "Submitting…" : confirmLabel}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
};

export { ConfirmSubmitDialog };

